import dayjs from "dayjs";
import { useEffect } from "react";
import {
  LoaderFunction,
  redirect,
  useLoaderData,
  useNavigate,
} from "react-router";

import { useConfetti } from "@/hooks";
import { get } from "api";
import { Models } from "api/types";
import { Button } from "atoms/button/Button";
import { Icon } from "atoms/icon/Icon";
import CameraFlip from "atoms/icon/icons/camera_flip.svg";
import Left from "atoms/icon/icons/chevron_left.svg";
import Right from "atoms/icon/icons/chevron_right.svg";
import Check from "atoms/icon/icons/ic_added_to_cart_check_ol.svg";
import OfferIcon from "atoms/icon/icons/ic_offers_ol.svg";
import { RepeatMDFooter } from "atoms/repeatmd-footer/RepeatMDFooter";
import { BadgeTag } from "atoms/tag/BadgeTag";
import { ActionModal } from "molecules/modal-window/action-modal/ActionModal";
import TopNav from "molecules/navbars/TopNav";
import { queryClient } from "pages/Root";
import { FormattedString } from "utils/FormattedStrings";

type LoaderData = {
  promotion: Models<"PromotionModel">;
  claimed: boolean;
  comingFromScan: boolean;
  isAdminImpersonation: boolean;
};

export const walletOfferLoader: LoaderFunction = async ({
  params: { offerId },
  request,
}) => {
  if (!offerId) return redirect("/wallet/offers");
  const searchParams = new URL(request.url).searchParams;
  const claimed = searchParams.has("claimed");
  const comingFromScan = searchParams.has("comingFromScan");

  const promotions = await queryClient.fetchQuery({
    queryKey: ["/promotions"],
    queryFn: () => get("/promotions"),
    staleTime: 1000 * 60 * 2,
  });

  const authClaims = await queryClient.fetchQuery({
    queryKey: ["/auth/claims"],
    queryFn: () => get("/auth/claims"),
  });

  const isAdminImpersonation = !!authClaims?.claims.adminUserId;

  const promotion = promotions.find((p) => p.id === offerId);

  if (!promotion?.redeemedOn) return redirect("/wallet/offers");

  return {
    promotion,
    claimed,
    comingFromScan,
    isAdminImpersonation,
  };
};

const WalletOfferPage = () => {
  const { promotion, claimed, comingFromScan, isAdminImpersonation } =
    useLoaderData() as LoaderData;
  const { triggerConfetti } = useConfetti();
  const navigate = useNavigate();

  const expiry = promotion.expiresOn ? dayjs(promotion.expiresOn) : null;

  useEffect(() => {
    if (claimed) {
      setTimeout(() => {
        triggerConfetti();
      }, 1000);
    }
  }, [claimed]);

  const secondButtonProps = !comingFromScan
    ? {
        onSecondButtonClick: () => navigate(""),
        secondButtonLabel: "View offer",
      }
    : {};

  return (
    <div className="flex min-h-full flex-col">
      <TopNav
        startIconSVG={Left}
        onStartIconClick={() => navigate("/wallet/offers")}
      >
        <h1 className="flex items-center text-xl">Offers</h1>
      </TopNav>
      <div className="relative flex h-full grow flex-col">
        {promotion.imageUrl && (
          <>
            {expiry && (
              <div className="absolute left-3 top-3">
                <BadgeTag color="alert">{`Expires ${expiry.fromNow()}`}</BadgeTag>
              </div>
            )}
            <img
              className="aspect-video w-full object-cover"
              src={promotion.imageUrl}
              alt="Promotion banner"
            />
          </>
        )}
        <div className="flex grow flex-col items-center gap-6 px-4 py-6 text-primary">
          <h1 className="font-serif text-h2">{promotion.title}</h1>
          <FormattedString>{promotion.description}</FormattedString>
          {!promotion.isInStoreRedeemable && (
            <>
              <div className="flex cursor-pointer items-center justify-center rounded bg-two p-4 text-center">
                <div className="flex items-center gap-3">
                  <div className="flex items-center">
                    <Icon svg={OfferIcon} size="xsmall" color="secondaryText" />
                  </div>
                  <div className="text-left  text-bold1 text-primary">
                    Congratulations! You have already redeemed this offer.
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-4 text-center text-secondary">
                <div className="text-center text-body1 text-secondary">
                  Redeemed:{" "}
                  {dayjs(promotion.redeemedOn).format("ddd, L - hh:mma")}
                </div>
                <p className="text-body2 italic text-secondary">
                  {promotion.disclaimer}
                </p>
                <Button
                  fullWidth
                  style="white"
                  onClick={() => navigate("/shop")}
                >
                  <div className="flex items-center">
                    <div>Explore shop</div>
                    <Icon color="brandColor" svg={Right} size="x2small" />
                  </div>
                </Button>
              </div>
            </>
          )}
          {promotion.isInStoreRedeemable && (
            <>
              <div className="flex cursor-pointer items-center justify-center rounded bg-two p-4 text-center">
                <div className="flex items-center">
                  <div className="flex items-center">
                    <Icon
                      svg={CameraFlip}
                      size="xsmall"
                      color="secondaryText"
                    />
                  </div>
                  <div className="mr-6 pl-2 text-left text-body1 text-primary">
                    Please show this page to a staff member to receive your
                    offer.
                  </div>
                </div>
              </div>
              <div className="text-center text-body1 text-secondary">
                Redeemed:{" "}
                {dayjs(promotion.redeemedOn).format("ddd, L - hh:mma")}
              </div>
              <div className="mb-5 rounded border p-2 text-center text-body1 text-secondary">
                In-office code: {promotion.promoCodeModel.code}
              </div>
              <p className="text-center text-body2 italic text-secondary">
                {promotion.disclaimer}
              </p>
            </>
          )}
        </div>
        <RepeatMDFooter />
      </div>
      {claimed && (
        <ActionModal
          open={true}
          size="medium"
          onClose={() => navigate("")}
          headerIconSvg={Check}
          headerIconColor="brandColor"
          title={`Congratulations! Your offer has been ${
            !comingFromScan && !isAdminImpersonation
              ? "applied to cart!"
              : "claimed!"
          }`}
          description={
            <div className="flex h-full flex-col items-center gap-12 px-2.5 text-center">
              <div className="flex grow flex-col gap-8">
                <div className="text-body1 text-secondary">
                  {!comingFromScan
                    ? "Explore our shop now to take advantage of this offer - it will automatically apply to your cart unless you remove it."
                    : "Tap below to see your confirmation and show this to a staff member in the store."}
                </div>
              </div>
            </div>
          }
          firstButtonLabel={!comingFromScan ? "Shop now" : "View confirmation"}
          onFirstButtonClick={() => {
            !comingFromScan ? navigate("/shop") : navigate("");
          }}
          {...secondButtonProps}
        />
      )}
    </div>
  );
};

export default WalletOfferPage;
